import { useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import { useEffect } from 'react';
import { CONFIRM_ACTIVITY_TIME } from '../../../config/confirmActivityModal';
import type { ConfirmActivityModalProps } from './confirmActivityModal.types';

/**
 * Runs the countdown of the confirm-activity modal shown during live ECG streaming.
 * While the modal is open it decrements time every second from CONFIRM_ACTIVITY_TIME, resets the
 * countdown when the modal closes, and calls handleStopStreaming once time reaches zero.
 *
 * @param props - Modal props carrying isOpen and handleStopStreaming.
 * @returns An object carrying the countdown state.
 */
export const useConfirmActivityModal = ({
  isOpen,
  handleStopStreaming,
}: Pick<ConfirmActivityModalProps, 'isOpen' | 'handleStopStreaming'>) => {
  const [state, setState] = useSetState({
    time: CONFIRM_ACTIVITY_TIME,
  });

  useEffect(() => {
    if (isOpen) {
      /**
       * Holds the timer that counts the confirm-activity modal's countdown down to zero. Created with setInterval at one-second steps, each tick decrementing time in the previous state.
       *
       * @returns The interval handle used to clear the countdown.
       */
      const interval = setInterval(() => {
        setState((prevState) => ({
          time: prevState.time - 1,
        }));
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }

    setState({ time: CONFIRM_ACTIVITY_TIME });

    return () => {};
  }, [isOpen]);

  useUpdateEffect(() => {
    if (state.time === 0) {
      handleStopStreaming();
    }
  }, [state.time]);

  return { state };
};
